/**
 * RoleWizardStepBasics.jsx — Employer v1.1 Phase 0.6 Slice B Step 1.
 *
 * Writes to `employer_roles.title` + `structured_definition_json`
 * via PATCH /employer/roles/{id}. Specifically owns:
 *   - title (required for first save)
 *   - must_have_domain (publish-blocker)
 *   - working_model (remote / hybrid / onsite)
 *   - location_constraint
 *   - comp_band (currency / min / max)
 *
 * Dealbreakers live in Step 3 per pre-code plan §2.3 + §2.4.
 *
 * Receives: title, definition, setTitle, setDefinition props from the
 * parent wizard page.
 */
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

export const WORKING_MODELS = ['remote', 'hybrid', 'onsite'];
export const COMP_CURRENCIES = ['GBP', 'USD', 'EUR', 'AED', 'QAR', 'SAR', 'CAD', 'AUD'];

function CompBandEditor({ value, onChange }) {
  const band = (value && typeof value === 'object') ? value : {};
  const update = (k, v) => {
    const next = { ...band };
    if (v === '' || v === null || v === undefined) {
      delete next[k];
    } else {
      next[k] = v;
    }
    onChange(Object.keys(next).length === 0 ? null : next);
  };
  return (
    <div className="grid grid-cols-3 gap-3" data-testid="basics-comp-band">
      <div>
        <Label className="text-xs text-slate-300">Currency</Label>
        <Select
          value={band.currency || ''}
          onValueChange={(v) => update('currency', v)}
        >
          <SelectTrigger data-testid="basics-comp-currency">
            <SelectValue placeholder="—" />
          </SelectTrigger>
          <SelectContent>
            {COMP_CURRENCIES.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs text-slate-300">Min</Label>
        <Input
          type="number"
          inputMode="numeric"
          min={0}
          value={Number.isFinite(band.min) ? String(band.min) : ''}
          onChange={(e) => {
            const v = e.target.value;
            update('min', v === '' ? '' : Math.max(0, parseInt(v, 10) || 0));
          }}
          data-testid="basics-comp-min"
        />
      </div>
      <div>
        <Label className="text-xs text-slate-300">Max</Label>
        <Input
          type="number"
          inputMode="numeric"
          min={0}
          value={Number.isFinite(band.max) ? String(band.max) : ''}
          onChange={(e) => {
            const v = e.target.value;
            update('max', v === '' ? '' : Math.max(0, parseInt(v, 10) || 0));
          }}
          data-testid="basics-comp-max"
        />
      </div>
    </div>
  );
}

export default function RoleWizardStepBasics({
  title,
  definition,
  onTitleChange,
  onDefinitionChange,
}) {
  const defn = definition || {};
  const patchDefn = (patch) => onDefinitionChange({ ...defn, ...patch });

  return (
    <div className="space-y-6 p-6" data-testid="wizard-step-basics">
      <header>
        <h2 className="text-base font-semibold text-ndor-ink">Basics</h2>
        <p className="text-sm text-slate-300">
          A title is required to save the draft. Everything else can stay
          empty until you're ready to publish.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="md:col-span-2">
          <Label htmlFor="basics-title" className="text-sm">
            Role title <span className="text-rose-200">*</span>
          </Label>
          <Input
            id="basics-title"
            value={title || ''}
            onChange={(e) => onTitleChange(e.target.value)}
            maxLength={200}
            placeholder="e.g. Senior Reservoir Engineer"
            data-testid="basics-title-input"
          />
        </div>

        <div>
          <Label htmlFor="basics-domain" className="text-sm">
            Must-have domain
          </Label>
          <Input
            id="basics-domain"
            value={defn.must_have_domain || ''}
            onChange={(e) => patchDefn({ must_have_domain: e.target.value || null })}
            maxLength={120}
            placeholder="e.g. upstream oil & gas"
            data-testid="basics-domain-input"
          />
          <p className="mt-1 text-xs text-slate-300">
            Required to publish. Drives the must-have filter at evaluation time.
          </p>
        </div>

        <div>
          <Label className="text-sm">Working model</Label>
          <Select
            value={defn.working_model || ''}
            onValueChange={(v) => patchDefn({ working_model: v || null })}
          >
            <SelectTrigger data-testid="basics-working-model">
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              {WORKING_MODELS.map((w) => (
                <SelectItem key={w} value={w} data-testid={`basics-working-model-${w}`}>
                  {w}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="md:col-span-2">
          <Label htmlFor="basics-location" className="text-sm">
            Location constraint
          </Label>
          <Input
            id="basics-location"
            value={defn.location_constraint || ''}
            onChange={(e) => patchDefn({ location_constraint: e.target.value || null })}
            maxLength={120}
            placeholder="e.g. Doha-based or UK-remote"
            data-testid="basics-location-input"
          />
        </div>

        <div className="md:col-span-2">
          <Label className="text-sm">Compensation band</Label>
          <CompBandEditor
            value={defn.comp_band}
            onChange={(v) => patchDefn({ comp_band: v })}
          />
          <p className="mt-1 text-xs text-slate-300">
            All three fields are required when any one is provided.
          </p>
        </div>
      </div>
    </div>
  );
}
